/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useState } from "react";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilState, useSetRecoilState } from "recoil";
import {
  CategoriesAPI,
  IDeleteManyCategoriesResponse,
} from "../../../api/Categories";
import {
  CategoriesAtom,
  IsCheckedAllCategoriesAtom,
} from "../../../atoms/CategoriesAtom";
import { CategoriesMoreActionsPopOverLoadingAtom } from "../../../atoms/MoreActionsPopoverAtom";
import {
  SelectedAllowedCategoriesAtom,
  SelectedCategoriesAtom,
} from "../../../atoms/SelectedCategoriesAtom";
import { TagsAtom } from "../../../atoms/TagsAtom";
import useClickOutside from "../../../hook/useClickOutside";
import { customTooltip, notSelect } from "../../../styles/general";
import Icon from "../../Icon";
import LoadingAbsolute from "../../LoadingAbsolute";
import Warning from "../../Modal/container/Warning";
import { useModal } from "../../Modal/Modal";
import StyledButton from "../../StyledButton";
import BatchDeleteCategories from "./BatchDelete/BatchDeleteCategories";

const Container = styled.div`
  position: relative;
  color: #2f2f2f;
  ${notSelect}
  height: 40px;
  width: 40px;
  display: grid;
  place-items: center;
`;

const Popover = styled.div`
  position: absolute;
  background: white;
  z-index: 1002;
  top: 40px;
  height: fit-content;
  width: 200px;
  overflow: auto;
  border-radius: 5px;
  box-shadow: 0px 3px 6px #00000026;
  border: 1px solid #d4d4d4;
  border-bottom: none;
  border-top: none;
  right: 0;
  display: flex;
  flex-direction: column;
  padding: 10px;
  svg,
  img {
    flex-shrink: 0;
  }
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  padding: 10px;
  font-size: 14px;
  color: #2f2f2f;
  cursor: pointer;
  span {
    margin-left: 7px;
  }
  svg {
    fill: #bbb;
  }
  &:hover {
    background-color: #fb972e;
    color: #ffffff;
    border-radius: 5px;
    svg {
      fill: #fff;
    }
  }
`;

const TooltipText = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0px;
  color: #ffffff;
`;

const Title = styled.div`
  color: #888888;
  font-size: 14px;
  padding: 10px 15px;
`;

const pathHover = css`
  :hover {
    svg {
      path {
        fill: #fff;
      }
    }
  }
`;

interface IActionsPopOver {}

export default function CategoriesBulkActions(
  props: IActionsPopOver
): JSX.Element {
  const [categoriesPool, setCategoriesPool] = useRecoilState(CategoriesAtom);
  const [openDropView, setOpenDropView] = useState<boolean>(false);
  const wrapperRef = useClickOutside(() => setOpenDropView(false));
  const [selectedCategories, setSelectedCategories] = useRecoilState(
    SelectedCategoriesAtom
  );
  const setTags = useSetRecoilState(TagsAtom);
  const setSelectedAllowedCategories = useSetRecoilState(
    SelectedAllowedCategoriesAtom
  );
  const setIsLoading = useSetRecoilState<boolean>(
    CategoriesMoreActionsPopOverLoadingAtom
  );
  const setIsCheckedAllCategories = useSetRecoilState(
    IsCheckedAllCategoriesAtom
  );
  const [isLoadingCheckCategories, setIsLoadingCheckCategories] =
    useState(false);
  const modal = useModal();

  async function deleteCategories(categoriesIds: string[]) {
    try {
      setIsLoading(true);
      const deleteCategoriesResponse: IDeleteManyCategoriesResponse =
        await CategoriesAPI.removeMany(categoriesIds);

      const thereAreCategoriesDeleted =
        deleteCategoriesResponse.deletedCategories.length;

      if (!!thereAreCategoriesDeleted) {
        const categoriesPoolWithoutDeletedOnes = categoriesPool.filter(
          (categoryFromPool) =>
            !deleteCategoriesResponse.deletedCategories.includes(
              categoryFromPool._id
            )
        );

        setTags((tags) =>
          tags.filter((tag) => !categoriesIds.includes(tag.categoryId))
        );

        setCategoriesPool(categoriesPoolWithoutDeletedOnes);
        clearAllSelects();
        toast("Categories deleted!", {
          type: "success",
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
      modal.popModal();
    }
  }

  function clearAllSelects() {
    setSelectedCategories([]);
    setSelectedAllowedCategories([]);
    setIsCheckedAllCategories(false);
  }

  async function handleCheckCategories() {
    try {
      setIsLoadingCheckCategories(true);

      const allCategoriesSelected = selectedCategories.map((category) =>
        categoriesPool.find((st) => st._id === category)
      );

      const userCategoriesSelected = allCategoriesSelected.filter(
        (category) => category.type !== "system"
      );

      const isSystemCategoriesSelected = !!allCategoriesSelected.filter(
        (category) => category.type === "system"
      ).length;

      const userCategoriesSelectedIds = userCategoriesSelected.map(
        (userCategory) => userCategory._id
      );

      if (!userCategoriesSelected.length) return { isEmpty: true };

      const response = await CategoriesAPI.checkCategoriesDelete(
        userCategoriesSelectedIds
      );

      if (
        !response.categoriesReadyToBeDeletedIds.length &&
        response.categoriesRestricted.length
      ) {
        toast("There are no categories eligible to be deleted", {
          type: "error",
        });
        setOpenDropView(false);
        return;
      }

      const categoriesReadyToBeDeleted =
        response.categoriesReadyToBeDeletedIds.length &&
        response.categoriesReadyToBeDeletedIds.map((category) =>
          categoriesPool.find((cat) => cat._id === category)
        );

      const categoriesRestricted =
        response.categoriesRestricted.length &&
        response.categoriesRestricted.map((category) =>
          categoriesPool.find((cat) => cat._id === category)
        );

      setSelectedAllowedCategories(
        categoriesReadyToBeDeleted.map((allowedCategory) => allowedCategory._id)
      );

      return {
        userCategoriesSelected,
        categoriesReadyToBeDeleted,
        categoriesRestricted,
        isSystemCategoriesSelected,
      };
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoadingCheckCategories(false);
    }
  }

  async function handleDelete() {
    try {
      const response = await handleCheckCategories();

      if ("isEmpty" in response) {
        modal.pushModal(
          <Warning
            icon={"./icons/delete.svg"}
            title="Delete Category"
            description={
              <div>
                <span>You have only selected system categories.</span>
                <br />
                <span>
                  <i>(System categories cannot be deleted)</i>
                </span>
              </div>
            }
            buttonsName={["Ok"]}
            onOkClicked={() => modal.popModal()}
            onCancelClicked={() => modal.popModal()}
          />
        );
        setOpenDropView(false);
        return;
      }

      if (
        response.categoriesRestricted.length &&
        !response.categoriesReadyToBeDeleted.length
      ) {
        setOpenDropView(false);
        toast("All categories selected can't be deleted", {
          type: "error",
        });
        return;
      }

      if (response.isSystemCategoriesSelected) {
        toast("System categories cannot be deleted", {
          type: "info",
        });
      }
      modal.pushModal(
        <BatchDeleteCategories
          categoriesReadyToBeDeleted={response.categoriesReadyToBeDeleted}
          categoriesRestricted={response.categoriesRestricted}
          allSelectedCategories={response.userCategoriesSelected}
          deleteCategories={(categoryIds) => {
            deleteCategories(categoryIds);
          }}
        />
      );
    } catch (error) {
      console.log(error);
    }
  }

  if (isLoadingCheckCategories) return <LoadingAbsolute />;

  return (
    <Container ref={wrapperRef}>
      <div
        style={{ cursor: "pointer" }}
        data-tip
        data-for="actions"
        onClick={() => setOpenDropView(!openDropView)}
      >
        <ReactTooltip
          id="actions"
          place="bottom"
          effect="solid"
          globalEventOff="click"
          backgroundColor="#333333"
          css={customTooltip}
        >
          <TooltipText>More Actions</TooltipText>
        </ReactTooltip>
        <StyledButton
          css={css`
            height: 40px;
            width: 40px;
          `}
          inverted
          onClick={() => setOpenDropView(!openDropView)}
          role="button"
          tabIndex={0}
        >
          <Icon name="threedots" />
        </StyledButton>
      </div>

      {openDropView && (
        <Popover>
          {selectedCategories.length > 1 && (
            <>
              <Title>BULK ACTIONS</Title>

              <Row css={pathHover} onClick={() => handleDelete()}>
                <Icon name="delete" width={16} height={16} />
                <span>Delete</span>
              </Row>
            </>
          )}
        </Popover>
      )}
    </Container>
  );
}
